<script>
import { mapState } from 'vuex';
import store from '@/utils/store';
import { loadUserModules } from '@/utils/moduleAccess';
import apiHelper from '@/utils/apiHelpers';
import socket from '@/plugins/socket';

export default {
    name: 'aside-component',
    data() {
        return {
            routerLinkActive: true,
			modules: [],
			openMainMenus: [],
			unreadChatRoomCount: 0,
			unreadRequestQuotationCount: 0,
			chatCountRefreshTimer: null,
			notificationRefreshInterval: null,
        }
    },
    computed: {
        ...mapState([
            'isLoggedIn',
            'loggedUser',
            'isOpenAside',
            'isDarkMode'
        ]),
    },
	watch: {
		'$route.path'() {
			this.autoExpandActiveModule();
			this.closeAsideOnSmallScreen();
		}
	},
	methods: {
		closeAsideOnSmallScreen() {
			if (window.matchMedia('(max-width: 992px)').matches && !this.isOpenAside) {
				store.commit('setToggleAsideMenu', true);
			}
		},
		async getUserModules() {
			try {
				this.modules = await loadUserModules(this.$router);
			} catch (error) {
				const alert = this.$refs.alert;
				if (alert && typeof alert.showAlert === 'function') {
					alert.showAlert('danger', 'Something went wrong. Please contact support.');
				}
				console.error('Error fetching modules by roles:', error);
			} finally {
				this.fetching = false;
			}
		},
		isChatModule(module) {
			return module?.route === '/chat-rooms';
		},
		isRequestQuotationModule(module) {
			return module?.route === '/request-quotations';
		},
		notificationCount(module) {
			if (this.isChatModule(module)) return this.unreadChatRoomCount;
			if (this.isRequestQuotationModule(module)) return this.unreadRequestQuotationCount;
			return 0;
		},
		formatNotificationCount(count) {
			return count > 999 ? '999+' : count;
		},
		hasChatModule() {
			return this.modules.some(module => this.isChatModule(module) || module.sub_modules?.some(sub => this.isChatModule(sub)));
		},
		hasRequestQuotationModule() {
			return this.modules.some(module => this.isRequestQuotationModule(module)
				|| module.sub_modules?.some(sub => this.isRequestQuotationModule(sub)));
		},
		async refreshUnreadChatRoomCount() {
			if (!this.hasChatModule()) {
				this.unreadChatRoomCount = 0;
				return;
			}

			try {
				const response = await apiHelper.get('/chats', this.$router);
				if (response?.success) {
					this.unreadChatRoomCount = response.chatrooms.filter(chat =>
						!chat.archived_at && (chat.messages || []).some(message => message.is_sender && !message.is_read)
					).length;
				}
			} catch (error) {
				console.error('Error fetching unread chat count:', error);
			}
		},
		async refreshUnreadRequestQuotationCount() {
			if (!this.hasRequestQuotationModule()) {
				this.unreadRequestQuotationCount = 0;
				return;
			}

			try {
				const response = await apiHelper.get('/request-quotations', this.$router);
				if (response?.success) {
					this.unreadRequestQuotationCount = response.quotations.filter(quotation =>
						!String(quotation.viewed_by || '').trim()
					).length;
				}
			} catch (error) {
				console.error('Error fetching unread request quotation count:', error);
			}
		},
		refreshNotificationCounts() {
			this.refreshUnreadChatRoomCount();
			this.refreshUnreadRequestQuotationCount();
		},
		handleChatListUpdated() {
			clearTimeout(this.chatCountRefreshTimer);
			this.chatCountRefreshTimer = setTimeout(() => {
				this.refreshUnreadChatRoomCount();
			}, 150);
		},
		isModuleActive(module) {
			if (module.sub_modules && module.sub_modules.length) {
				return module.sub_modules.some(sub => this.$route.path.startsWith(sub.route));
			}
			return this.$route.path.startsWith(module.route);
		},
		autoExpandActiveModule() {
			const matchedModule = this.modules.find(module =>
				module.sub_modules?.some(sub => this.$route.path.startsWith(sub.route))
			);

			if (matchedModule) {
				// Only keep this module open, collapse others
				this.openMainMenus = this.openMainMenus.includes(matchedModule.id)
					? this.openMainMenus.filter(id => id === matchedModule.id)
					: [...this.openMainMenus, matchedModule.id];
			}
		}
		,
        toggleDarkMode() {
            store.commit('setToggleDarkMode', !this.isDarkMode);
        },
		toggleMainMenu(id) {
	const index = this.openMainMenus.indexOf(id);

	if (index > -1) {
		// If it's already open, close it
		this.openMainMenus.splice(index, 1);
	} else {
		// Otherwise, open it (but do NOT close others)
		this.openMainMenus.push(id);
	}
},
        toggleCloseMainMenu(){
            this.routerLinkActive = true;
            this.openMainMenus = [];
        },
    },
    created() {
		this.closeAsideOnSmallScreen();
		window.addEventListener('resize', this.closeAsideOnSmallScreen);
		this.getUserModules().then(() => {
			this.autoExpandActiveModule();
			this.refreshNotificationCounts();
			this.notificationRefreshInterval = window.setInterval(this.refreshUnreadRequestQuotationCount, 30000);
		});
		socket.on('chatListUpdated', this.handleChatListUpdated);
		window.addEventListener('focus', this.refreshNotificationCounts);
		window.addEventListener('requestQuotationViewed', this.refreshUnreadRequestQuotationCount);
	},
	beforeUnmount() {
		clearTimeout(this.chatCountRefreshTimer);
		clearInterval(this.notificationRefreshInterval);
		window.removeEventListener('resize', this.closeAsideOnSmallScreen);
		window.removeEventListener('focus', this.refreshNotificationCounts);
		window.removeEventListener('requestQuotationViewed', this.refreshUnreadRequestQuotationCount);
		socket.off('chatListUpdated', this.handleChatListUpdated);
	}
};
</script>
